import React, { useMemo } from 'react'
import Membership from '../../dtos/Membership';
import PrimaryButton from '../../common/components/PrimaryButton';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatDate } from '../../common/components/FormattedDate';
import { useLocation } from 'react-router-dom'
import { useIsMobile } from '../../hooks/useIsMobile';
import { DEFAULT_IMAGE } from '../../common/constants';
import GenderRestrictions from "../GenderRestrictions";
import OfferPrice from '../groupsAndOffers/complete-sessions/OfferPrice';

interface IMembershipItemProps {
  membership: Membership
  onSelection: (membershipId: number) => void
  altTranslationKeys?: string[]
}

export const MembershipItem: React.FC<IMembershipItemProps> = ({ membership, onSelection, altTranslationKeys = [] }) => {
  const classes = ["membership-informations"];
  if (membership.type_status_id !== "OK") {
    classes.push("is-preview");
  }

  const { t } = useTranslation()

  const location = useLocation()
  const isMobile = useIsMobile()

  const viewMoreKey = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get('viewMoreButton')) {
      return searchParams.get('viewMoreButton') || 'viewMoreButton'
    }

    return altTranslationKeys.includes('memberships.viewMore')
      ? 'memberships.viewMoreAlt'
      : 'memberships.viewMore'
  }, [altTranslationKeys, location])

  const targetMemberMessage =membership.family_membership ? t('memberships.type.family_membership') : t('memberships.type.non_family_membership');
  const viewMembershipAge = useMemo(() => {
    const isYear = membership.restriction_age_display === 'year';
    const minAge = isYear && null !== membership.restriction_age_in_month_min ? Math.round(membership.restriction_age_in_month_min / 12) : membership.restriction_age_in_month_min;
    const maxAge = isYear && null !== membership.restriction_age_in_month_max ? Math.floor(membership.restriction_age_in_month_max / 12) : membership.restriction_age_in_month_max;
    const ageTextValue = {
      max_age: maxAge,
      min_age: minAge,
      target_member: targetMemberMessage,
      max_age_unit: isYear ? t('memberships.age.year', {count: maxAge ?? 0}) : t('memberships.age.month', {count: maxAge ?? 0}),
      min_age_unit: isYear ? t('memberships.age.year', {count: minAge ?? 0}) : t('memberships.age.month', {count: minAge ?? 0})
    };

    return (minAge && maxAge && t('memberships.age.range', ageTextValue))
      || (minAge && t('memberships.age.min_only', ageTextValue))
      || (maxAge && t('memberships.age.max_only', ageTextValue))
    ;
  }, [membership, t, targetMemberMessage])

  return (
    <MembershipItemContainer className={classes.join(" ")}>
      <div>
        <MembershipImage src={membership.media?.path || DEFAULT_IMAGE} alt={membership.media?.text_alt || ''} />
        <h3>{membership["name"]}</h3>
        <div className="membership__details">
          <div dangerouslySetInnerHTML={{ __html: membership.description }}></div>
          <ul className="membership__properties">
            <li>
                <b>{t('memberships.property.price_minimum') + t('column')}</b>
                <OfferPrice
                    price={membership.lowest_price}
                    isFirstLetterCapital={false}
                    withStyle={false}
                />
            </li>
            <li>
              <b>{t('memberships.property.start')+ t('column')}</b>
              {
                membership.active_period.start_on === 'on_purchase' &&
                t('memberships.start_type.BUY')
              }
              {
                membership.active_period.start_on === 'on_first_dropin_registration' &&
                t('memberships.start_type.FIRST_USE')
              }
              {
                membership.active_period.start_on === 'custom_date' &&
                t('memberships.start_type.FIXED', {
                  start_date: formatDate(membership.active_period.start_on_custom_date, { isShortDate: isMobile })
                })
              }
            </li>
            {
                membership.active_period.end_on === 'custom_date' &&
                membership.active_period.end_on_custom_date &&
                <li>
                    <b>{t('memberships.property.end') + t('column')}</b>
                    {
                        t('memberships.end_type.FIXED', {
                        end_date: formatDate(
                            membership.active_period.end_on_custom_date, 
                            { isShortDate: isMobile }
                        )
                        })
                    }
                </li>
            }
            {
                membership.active_period.end_on === 'after' &&
                membership.active_period.end_after &&
                <li>
                    <b>{t('memberships.property.end') + t('column')}</b>
                    {
                        t('memberships.end_type.AFTER', {
                            end_after: membership.active_period.end_after.value,
                            unit: t(
                                'memberships.unit.' + membership.active_period.end_after.unit,
                                {count: parseInt(membership.active_period.end_after.value)}
                            )
                        })
                    }
                </li>
            }
            {
              membership.family_membership &&
              <span className="property-freeform">{t('memberships.property.family')}</span>
            }
            {
              (membership.gender_restrictions)
              &&
                <li>
                    <b>{t('memberships.property.gender') + t('column')}</b>
                    <GenderRestrictions
                        genders={membership.gender_restrictions}
                        forPassModal={false}
                        translation={t}
                        extraInfoMessage={" " + targetMemberMessage}
                    />
                </li>
            }
            {
              (membership.restriction_age_in_month_min || membership.restriction_age_in_month_max)
              &&
                <li>
                  <b>{t('memberships.property.age')+ t('column')}</b>
                  { viewMembershipAge }
                </li>
            }
          </ul>
        </div>
      </div>
      <ViewMoreContainer>
        <PrimaryButton
          text={t(viewMoreKey)}
          onClick={() => onSelection(membership.id)}
        />
      </ViewMoreContainer>
    </MembershipItemContainer>
  );
};

const GUTTER = 20
const IMAGE_SIZE = 50

const MembershipItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  & + & {
    border-top: 1px solid black;
  }
	cursor: pointer;
	&:hover, &:active, &:focus, &.is-selected {
		background: back;
	}

	h3 {
		margin-bottom: ${GUTTER / 2}px;
	}

	.is-formatted-text {
		p {
			margin-bottom: 0.6em;
		}
	}

	.membership__properties {
		&, & > li {
			margin: 0;
			padding: 0;
			list-style-type: none;
		}
		.property-key {
			font-weight: bold;
		}
	}
`

const MembershipImage = styled.img`
  border: 1px solid black;
  border-radius: 2px;
  overflow: hidden;
  float: left;
  margin-right: ${GUTTER}px;

  &, .fake-image {
    width: ${IMAGE_SIZE}px;
    height: ${IMAGE_SIZE}px;
    .fake-image--image-element {
      background-size: cover;
    }
  }

  &.fake-image.is-error>.fake-image--image-element {
    background: black;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
  }
`

const ViewMoreContainer = styled.div`
  margin-left: ${GUTTER}px;
  min-width: 200px;
`
